import React, { useState, useEffect } from "react";
import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  ThemeProvider,
  CssBaseline,
  Backdrop,
  CircularProgress,
} from "@mui/material";
import { Visibility } from "@mui/icons-material";
import { GET_TESTCASES_REPORT_BY_REPORTID_URL, } from "envConfig";
import { Logger } from "utils/Logger";
import { useNavigate, useParams } from "react-router-dom";
import { TestCase } from "../../Interfaces/AutomateTest";
import { useTheme } from "@emotion/react";

export const AllTestCasesReport: React.FC = () => {
  const theme = useTheme();
  const { reportId, tenantId, testSuiteId } = useParams<{ reportId: string, tenantId: string, testSuiteId: string }>();
  const [testCasesReports, setTestCasesReports] = useState<TestCase[]>([]);
  const [loading, setLoading] = useState<boolean>(false);  
  const navigate = useNavigate();

    const fetchTestCasesReports = async () => {
      try {
        setLoading(true);
        const response = await fetch(GET_TESTCASES_REPORT_BY_REPORTID_URL + `/${reportId}/testCases`, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
            Logger.error("st-ck-client/src/modules/AutomateTest/AllTestCasesReport/AllTestCasesReport.tsx : fetchTestCasesReports(): issue while getting TestCases Report, response is " , response.statusText);
            setLoading(false)
            throw new Error(`Error: ${response.status}`);
        }

        const data = await response.json();
        if (Array.isArray(data.data)) {
            setTestCasesReports(data.data);
            setLoading(false);
            Logger.info("st-ck-client/src/modules/AutomateTest/AllTestCasesReport/AllTestCasesReport.tsx : fetchTestCasesReports(): TestCases Reports " , JSON.stringify(data.data, null, 2));
        } else {
            Logger.error("st-ck-client/src/modules/AutomateTest/AllTestCasesReport/AllTestCasesReport.tsx : fetchTestCasesReports(): ", "Unexpected API response format: " + JSON.stringify(data.data, null, 2));
            setTestCasesReports([]);
            setLoading(false);
        }
      } catch (error) {
        Logger.error("st-ck-client/src/modules/AutomateTest/AllTestCasesReport/AllTestCasesReport.tsx : fetchTestCasesReports(): ", "Failed to fetch TestCases report: "+ error);
        setTestCasesReports([]);
        setLoading(false);
      }
    };

    useEffect(() => {
      fetchTestCasesReports();
    }, []);

  return (loading ?
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Backdrop
        sx={(theme) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </ThemeProvider>
    :
    <Box pl={{ xs: 1.25, sm: 2.5 }} pt={1} width="100%">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
      <Typography variant="h4" gutterBottom>
        Suite - All TestCases Reports
      </Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>TESTCASE</TableCell>
              <TableCell>DESCRIPTION</TableCell>
              <TableCell>TOTAL STEPS</TableCell>
              <TableCell>STATUS</TableCell>
              <TableCell>ACTION</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {testCasesReports.map((testCaseReport, index) => (
              <TableRow key={testCaseReport._id}>
                <TableCell>{index}</TableCell>
                <TableCell>{testCaseReport.name}</TableCell>
                <TableCell>{testCaseReport.description}</TableCell>
                <TableCell>{[testCaseReport.testSteps].length || 0}</TableCell>
                <TableCell>{testCaseReport.status}</TableCell>
                <TableCell>
                    <>
                  <IconButton onClick={() => navigate(`/report/${reportId}/tenant/${tenantId}/testSuite/${testSuiteId}/testCase/${testCaseReport._id}`)}>
                    <Visibility />
                  </IconButton>
                  </>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
